import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {useSelector} from 'react-redux';
import { CircularProgress, styled, Box, CardMedia, Typography } from '@mui/material';

import {axiosCached} from "../../../../redux/auth/authService";
import NoImage from '../../../../styles/images/NoProjectImageFound.jpg';

// Custom Utilities
import {formatTypographyColor} from '../../../../utilities/FormatUtilities';

// Custom Components
import SmartButton from '../SmartButton';

const PREFIX = 'SmartCarouselItem';

const classes = {
    metadataContainerBottom: `${PREFIX}-metadataContainerBottom`,
    metadataContainerLeft: `${PREFIX}-metadataContainerLeft`,
    metadataTextContainerBottom: `${PREFIX}-metadataTextContainerBottom`,
    metadataTextContainerLeft: `${PREFIX}-metadataTextContainerLeft`,
    grayBg: `${PREFIX}-grayBg`,
    lightGrayBg: `${PREFIX}-lightGrayBg`,
    defaultBgLight: `${PREFIX}-defaultBgLight`,
    defaultBgDark: `${PREFIX}-defaultBgDark`,
    metadataButtonContainer: `${PREFIX}-metadataButtonContainer`,
    media: `${PREFIX}-media`,
    mediaNoMetadataContainer: `${PREFIX}-mediaNoMetadataContainer`,
    mediaLoading: `${PREFIX}-mediaLoading`,
    loadingContainer: `${PREFIX}-loadingContainer`
};

const StyledBox = styled(Box)((
    {
        theme
    }
) => ({
    // root: {
    //     height: '100%',
    // },
    // linearProgress: {
    //     height: '1px'
    // },
    [`& .${classes.metadataContainerBottom}`]: {
		minHeight: '100px',
		width: '100%',
		position: 'absolute',
		display: 'flex',
		left: '0',
		bottom: '0',
		padding: '0 0 0 0',
		zIndex: '10',
		//backgroundImage: 'linear-gradient(to top, rgba(224,224,224,1) 0%, rgba(238,238,238,1) 25%, rgba(245,245,245,1) 100%)'
	},

    [`& .${classes.metadataContainerLeft}`]: {
        width: '30%',
        height: '100%',
        maxHeight: '100%',
        overflowY: 'auto',
        display: 'flex',
        position: 'absolute',
        left: 0,
        top: 0,
        color: 'white',
    },

    [`& .${classes.metadataTextContainerBottom}`]: {
		margin: '0 0 0 15px',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center'
	},

    [`& .${classes.metadataTextContainerLeft}`]: {
        margin: '0px 60px 0px 100px',
        overflowY: "auto",
        maxHeight: "330px",
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    },

    [`& .${classes.grayBg}`]: {
        backgroundColor: "rgb(51, 50, 50, .90)" //"#333232",
    },

    [`& .${classes.lightGrayBg}`]: {
		backgroundColor: "rgb(103, 103, 103,.90)"
	},

    [`& .${classes.defaultBgLight}`]: {
		backgroundImage: 'linear-gradient(to top, rgba(224,224,224,1) 0%, rgba(238,238,238,1) 25%, rgba(245,245,245,1) 100%)'
	},

    [`& .${classes.defaultBgDark}`]: {
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'linear-gradient(rgba(255, 255,255, 0.05), rgba(255, 255, 255, 0.05))'
    },

    [`& .${classes.metadataButtonContainer}`]: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },

    [`& .${classes.media}`]: {
        height: '100%',
        borderTopRightRadius: '4px',
        borderTopLeftRadius: '4px',
    },

    [`& .${classes.mediaNoMetadataContainer}`]: {
        height: '100%',
        width: '100%',
        borderTopRightRadius: '4px',
        borderTopLeftRadius: '4px',
        borderBottomRightRadius: '4px',
        borderBottomLeftRadius: '4px',
    },

    // [`& .${classes.mediaLoading}`]: {
    //     position: 'relative',
    //     width: '100%',
    //     height: '100%',
    // },

    [`& .${classes.loadingContainer}`]: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: '0px'
    }
}));

export default function SmartCarouseItem({carouselItem, configuration, height}) {
    const { t } = useTranslation();

    const config = useSelector(state => state.config);
    const data = useSelector(state => state.data);
	const persist = useSelector(state => state.persist);
	const isThemeLight = persist.themeLight;

    // const persist = useSelector(state => state.persist);

    // https://jacobsengineering.sharepoint.com/sites/CPOV012053/HomeImages/Forms/HomeImages.aspx

    //     configuration: {
    //          itemHeight: <parent height>
    //          button: {
    //              dataSourceName: <datasource name for button>
    //              size:
    //              color:
    //              variant:
    //              alignItems:
    //              justifyContent:
    //          }
    //          image: {
    //              dataSourceName: <table name for image>
    //              identifierFieldName: <identifier field name from image datasource>
    //              size: small | medium | large (assumes SMImage, MDImage, LGImage are the field names)
    //              backgroundSize: contain | cover
    //          }
    //          title: {
    //              display: true | false
    //              color: <title color set in smart carousel>
    //              variant: <title variant set in smart carousel>
    //          }
    //          description: {
    //              display: true | false
    //              color: <description color set in smart carousel>
    //              variant: <description variant set in smart carousel>
    //          }
	//			titleDescriptionPosition: left | bottom
    //      }

    // const itemProps = {
    //     itemHeight: carouselItemHeight,
    //     image: {
    //         dataSourceName: imageTableName,
    //         identifierFieldName: identifierField,
    //     },
    // };

    // const carouselItemHeight = configuration.itemHeight;
    // const carouselItemBackgroundColor = configuration.backgroundColor;
    // -- Title
    const titleDisplay = configuration.title.display;
    const titleColor = configuration.title.color;
    const titleVariant = configuration.title.variant;
    // -- Description
    const descriptionDisplay = configuration.description.display;
    const descriptionColor = configuration.description.color;
    const descriptionVariant = configuration.description.variant;
	// -- Metadata Text Container
	const metadataTextContainer = configuration.metadataTextContainer;
    // -- Image
    const imageTableName = configuration.image.imageTableName;
    const imageIDPublished = configuration.image.imageIDPublished;
    const imageIDStaged = configuration.image.imageIDStaged;
    // const imageIdentifierField = configuration.image.identifierFieldName;
    const imageSize = configuration.image.size;
    const imageBackgroundSize = configuration.image.backgroundSize;

    // App State

    // Component State
    const [image, setImage] = useState(null);
    const [titleTypographyColor, setTitleTypographyColor] = useState(formatTypographyColor(titleColor));
    const [descriptionTypographyColor, setDescriptionTypographyColor] = useState(formatTypographyColor(descriptionColor));
	const [metadataTextContainerStyle, setMetadataTextContainerStyle] = useState('');
    const [imageLoading, setImageLoading] = useState(true);
    // const [showProgress, setShowProgress] = useState(showFetching !== undefined ? showFetching : true);

    useEffect(() => {
		setImageLoading(true);
        let isSubscribed = true;    // https://juliangaramendy.dev/blog/use-promise-subscription

        // Get image parameters from config for axios call
        let imageParams = getImageParameters(imageSize);

        const url = config.data.authentication.api + 'DBQuery';

		axiosCached.get(url, {
            params: {
                db: config.data.authentication.apiSource,
                table: '{0}{1}'.format(imageTableName, data.viewStaged ? 'Staged': ''),
                fields: imageParams.targetFieldName,
                where: '{0} eq {1}'.format(data.viewStaged ? imageIDStaged : imageIDPublished, carouselItem.identifier) // TODO: config
            },
            useCache: true
        })
            .then(function (response) {
                if (isSubscribed) {
                    // console.log('-----> SmartCarouselItem:useEffect(): Response: ', response);
                    const imageBase64 = 'data:image/png;base64,' + response.data[0][imageParams.responseName];
                    setImage(imageBase64);
                }
            })
            .catch(function (error) {
                // console.log('-----> SmartCarouselItem:useEffect(): Error: ', error);
                setImage(NoImage);
            });

        return () => isSubscribed = false;
    }, [carouselItem.identifier]);

    useEffect(() => {

        if (image) {
            setImageLoading(false);
        } else {
            setImageLoading(true);
        }
    }, [image]);

	useEffect(() => {
		if (metadataTextContainer.useTheme) {
			if (isThemeLight) {
				setMetadataTextContainerStyle(classes.lightGrayBg);
			} else {
				setMetadataTextContainerStyle(classes.grayBg);
			}
		} else { // Used for home page, basic role, carousel
            if (isThemeLight) {
                setMetadataTextContainerStyle(classes.defaultBgLight);
            } else {
                setMetadataTextContainerStyle(classes.defaultBgDark);
            }
		}
	},[isThemeLight])

    const getImageParameters = (value) => {
        // console.log('-----> SmartCarouselItem:getImageParameters()');

        let targetFieldName = '';
        let responseName = '';

        switch(value.toLowerCase()) {
            case 'small':
                targetFieldName = 'SMImage';
                responseName = 'smImage';
                break;
            case 'medium':
                targetFieldName = 'MDImage';
                responseName = 'mdImage';
                break;
            case 'large':
                targetFieldName = 'LGImage';
                responseName = 'lgImage';
                break;
            default:
                targetFieldName = 'MDImage';
                responseName = 'mdImage';
        }

        const imageParams = {
            targetFieldName: targetFieldName,
            responseName: responseName
        }

        // console.log('-----> SmartCarouselItem:getImageParameters(): imageParams', imageParams);
        return imageParams;
    }

    return (
        <StyledBox style={{height: height, position: "relative" }}>

            {(titleDisplay && descriptionDisplay) &&
            	// Using a metadata container and rounding just the top corners of the image
				<Box style={{height: '100%'}}>
					{imageLoading &&
						<div className={classes.loadingContainer} >
							<div style={{textAlign: 'center'}}>
								<CircularProgress />
							</div>
						</div>
					}

					{!imageLoading &&
						<CardMedia className={classes.media} style={{backgroundSize: imageBackgroundSize}} image={image}/>
					}

					{carouselItem.buttonEnabled &&
						// Item has a button
						<Box className={`${metadataTextContainer.position === "left" ? classes.metadataContainerLeft : classes.metadataContainerBottom} ${carouselItem.title || carouselItem.description ? metadataTextContainerStyle : ""}`}>
							<Box className={`${metadataTextContainer.position === "left" ? classes.metadataTextContainerLeft : classes.metadataTextContainerBottom}`} style={{width: '75%'}}>
								{carouselItem.title &&
									<Box>
										<Typography color={titleTypographyColor.component} variant={titleVariant} style={{color: titleTypographyColor.style}}>
											{t(carouselItem.title)}
										</Typography>
									</Box>
								}
								{carouselItem.description &&
									<Box>
										<Typography color={descriptionTypographyColor.component} variant={descriptionVariant} style={{color: descriptionTypographyColor.style}}>
											{t(carouselItem.description)}
										</Typography>
									</Box>
								}
							</Box>
							<Box className={classes.metadataButtonContainer} style={{width: '25%'}}>
								<SmartButton configuration={carouselItem.buttonConfiguration}/>
							</Box>
						</Box>
					}

					{!carouselItem.buttonEnabled &&
						// Item doesn't have a button
						<Box className={`${metadataTextContainer.position === "left" ? classes.metadataContainerLeft : classes.metadataContainerBottom} ${carouselItem.title || carouselItem.description ? metadataTextContainerStyle : ""}`}>
							<Box className={`${metadataTextContainer.position === "left" ? classes.metadataTextContainerLeft : classes.metadataTextContainerBottom}`} style={{width: '100%'}}>
								{carouselItem.title &&
									<Box>
										<Typography color={titleTypographyColor.component} variant={titleVariant} style={{color: titleTypographyColor.style}}>
											{t(carouselItem.title)}
										</Typography>
									</Box>
								}
								{carouselItem.description &&
									<Box>
										<Typography color={descriptionTypographyColor.component} variant={descriptionVariant} style={{color: descriptionTypographyColor.style}}>
											{t(carouselItem.description)}
										</Typography>
									</Box>
								}
							</Box>
						</Box>
					}
				</Box>
            }

            {(!titleDisplay || !descriptionDisplay) &&
				// Not using a metadata container and rounding all the corners of the image: Issue: image being cropped from bottom so not rounding images??
				<Box style={{height: '100%'}}>
					{imageLoading &&
						<div className={classes.loadingContainer}>
							<div style={{textAlign: 'center'}}>
								<CircularProgress />
							</div>
						</div>
					}
					{!imageLoading &&
						<CardMedia className={classes.mediaNoMetadataContainer} style={{backgroundRepeat: 'no-repeat'}} image={image}/>
					}
				</Box>
            }

        </StyledBox>
    );
}
